import Button from "components/Buttons/Button";
import ModalComponent from "components/Common/Modal";
import Render from "components/Common/Render";
import Stepper from "components/Tutorials/InstantPayTutorial.tsx/Stepper";
import StepRenderer from "components/Tutorials/InstantPayTutorial.tsx/StepRenderer";
import { downloadQrApi } from "constants/apiUrls";
import { InstantPayModalDataType } from "containers/Home";
import { OS } from "enum/common";
import { useMobileOS } from "hooks/useDetectOS";
import { useAppSelector } from "hooks/useRedux";
import { http } from "middleware/apiMiddleware";
import { useCallback, useState } from "react";
import toast from "react-hot-toast";

import { RootState } from "store";
import { fileDownload } from "utils/common";

export enum InstantPayTutorialStepEnum {
  SETUP = "setup",
  ACCESS = "access",
  QRC0DE = "QRCODE",
}

export type InstantPayTutorialStep = {
  step: InstantPayTutorialStepEnum;
  label: string;
  id: number;
};

const steps = [
  {
    id: 1,
    step: InstantPayTutorialStepEnum.QRC0DE,
    label: "How it works",
  },
  {
    id: 2,
    step: InstantPayTutorialStepEnum.SETUP,
    label: "Set Up Your Payout Method",
  },
  {
    id: 3,
    step: InstantPayTutorialStepEnum.ACCESS,
    label: "Access Your Instant Pay QR Code",
  },
];

interface IInstapPayututorialsProps {
  showInstantpayTutorial: InstantPayModalDataType;
  setShowInstantpayTutorial: (value: InstantPayModalDataType) => void;
  onSuccess: () => void;
}

const InstantPayTutorials: React.FC<IInstapPayututorialsProps> = ({
  showInstantpayTutorial,
  setShowInstantpayTutorial,
  onSuccess,
}) => {
  const [selectedStep, setSelectedStep] = useState<InstantPayTutorialStep>(
    steps[0]
  );
  const [businessQR, setBusinessQR] = useState("");
  const [lastCompletedStep, setLastCompletedStep] = useState(0);
  const [activatingInstantPay, setActivatingInstantPay] = useState(false);
  const [downloadingQr, setDownloadingQr] = useState(false);

  const os = useMobileOS();

  const selectedBusiness = useAppSelector(
    (state: RootState) => state.businesses.business
  );

  const getFooterPaddingbasedonOS = () => {
    if (os === OS.ANDROID) {
      return "mb-4";
    } else if (os === OS.IOS) {
      return "mb-8";
    } else {
      return "mb-0";
    }
  };

  const onFinish = () => {
    onSuccess && onSuccess();
  };

  const onTutorialClose = () => setShowInstantpayTutorial(null);

  const onActivateInstantPay = useCallback(async () => {
    try {
      setActivatingInstantPay(true);
      const { data, error } = await http.patch(
        `/business/${selectedBusiness?.uuid}/business-paid-modules/${showInstantpayTutorial?.moduleId}`,
        {
          enabled: true,
        }
      );

      if (!!data && !error) {
        setBusinessQR(data?.entityQrCode);
        setSelectedStep(steps[2]);
      }
    } catch (error) {
      console.error("error activating instant pay", error);
      toast.error(
        "Unexpected Error occurred while activating module, Please contact the app admin"
      );
    } finally {
      setActivatingInstantPay(false);
    }
  }, []);

  const onNextStep = () => {
    const nextStep =
      steps?.filter((step) => step?.id === selectedStep?.id + 1)[0] || null;
    console.log(nextStep, "nextStep");
    if (nextStep?.id === 2) {
      setSelectedStep(nextStep);
    } else if (nextStep?.id === 3) {
      onActivateInstantPay();
    }
  };

  const downloadQR = async () => {
    try {
      setLastCompletedStep(3);
      setDownloadingQr(true);
      const { data, error } = await http.get(
        downloadQrApi(selectedBusiness?.uuid || "")
      );
      if (error) {
        toast.error("Unexpected Error occured while generating qrcode");
      }
      if (data) fileDownload(data?.entityQrCode);
    } catch (error) {
      toast.error("Unexpected Error occured while generating qrcode");
    } finally {
      setDownloadingQr(false);
    }
  };

  const allStepFooter = (
    <div className="justify-end flex gap-4">
      <Button
        label={"Cancel"}
        variant="secondary"
        customClass="md:w-[200px] w-[100%] !me:0"
        onClick={onTutorialClose}
      />
      <Button
        loading={activatingInstantPay}
        label="Continue"
        disabled={
          selectedStep?.id !== lastCompletedStep &&
          selectedStep?.id < steps?.length
        }
        onClick={onNextStep}
        customClass="md:w-[200px] w-[100%] !me:0"
      />
    </div>
  );

  const lastStepFooter = (
    <div className="justify-end flex gap-4">
      <Button
        disabled={downloadingQr}
        label={"Download QR"}
        variant="secondary"
        customClass="md:w-[200px] w-[100%] !me:0"
        onClick={downloadQR}
      />
      <Button
        onClick={onFinish}
        label="Finish"
        customClass="md:w-[200px] w-[100%] !me:0"
      />
    </div>
  );

  return (
    <Render when={!!showInstantpayTutorial}>
      <ModalComponent
        showClose
        allowBodyScroll
        renderHeader={<>Instant Pay Activation</>}
        renderFooter={
          <div className={getFooterPaddingbasedonOS()}>
            {selectedStep?.id < steps?.length ? allStepFooter : lastStepFooter}
          </div>
        }
        containerClass="md:w-[85vw] md:max-w-[1200px] md:h-[85vh] h-[100vh] w-[100vw] relative !overflow-hidden"
        onClose={() => setShowInstantpayTutorial(null)}
      >
        <div className="flex flex-1 md:gap-8 gap-2 overflow-hidden md:flex-row flex-col">
          <Stepper
            steps={steps}
            selectedStep={selectedStep}
            setSelectedStep={setSelectedStep}
            lastCompletedStep={lastCompletedStep}
          />

          <div
            className={`overflow-hidden ${
              selectedStep.id !== 1 ? "flex flex-1" : ""
            }`}
          >
            <StepRenderer
              businessUuid={selectedBusiness?.uuid || ""}
              qrUrl={businessQR}
              selectedStep={selectedStep}
              setLastCompletedStep={setLastCompletedStep}
            />
          </div>
        </div>
      </ModalComponent>
    </Render>
  );
};

export default InstantPayTutorials;
